import React, { useCallback } from 'react';
import {
  Box, Typography, Button, IconButton, ToggleButton, ToggleButtonGroup, Stack,
  useTheme, useMediaQuery, Popover, alpha, CircularProgress
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  CalendarMonth as CalendarMonthIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { ToolbarProps, Views } from 'react-big-calendar';
import { format as dateFnsFormat } from 'date-fns';
import { RBCEvent } from '../types';
import { DEFAULT_AGENDA_LENGTH } from '../constants';

interface CustomToolbarProps extends ToolbarProps<RBCEvent> {
  onDateRangeApply?: (start: Date, end: Date) => void;
  currentDate?: Date;
  currentAgendaLength?: number;
  isLoading?: boolean;
}

export const CustomToolbar = (props: CustomToolbarProps) => {
  const { label, onNavigate, onView, view, onDateRangeApply, currentDate, currentAgendaLength, isLoading } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Popover state for date range picker
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [rangeStart, setRangeStart] = React.useState('');
  const [rangeEnd, setRangeEnd] = React.useState('');

  const handleLabelClick = (e: React.MouseEvent<HTMLElement>) => {
    if (view === Views.AGENDA) {
      // Pre-fill with the actual current range
      // agendaLength=N means RBC shows start through start+N (inclusive end)
      const startDate = currentDate || new Date();
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + (currentAgendaLength ?? DEFAULT_AGENDA_LENGTH));
      setRangeStart(dateFnsFormat(startDate, 'yyyy-MM-dd'));
      setRangeEnd(dateFnsFormat(endDate, 'yyyy-MM-dd'));
      setAnchorEl(e.currentTarget);
    }
  };

  const handleApply = useCallback(() => {
    if (rangeStart && rangeEnd && onDateRangeApply) {
      const start = new Date(rangeStart + 'T00:00:00');
      const end = new Date(rangeEnd + 'T23:59:59');
      if (start <= end) {
        onDateRangeApply(start, end);
      }
      setAnchorEl(null);
    }
  }, [rangeStart, rangeEnd, onDateRangeApply]);

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: isMobile ? 'stretch' : 'center',
      justifyContent: 'space-between',
      mb: 2,
      gap: 1.5
    }}>
      {/* Navigation */}
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'space-between' : 'flex-start' }}>
        <Button
          variant="outlined"
          size="small"
          onClick={() => onNavigate('TODAY')}
          startIcon={<TodayIcon sx={{ fontSize: 16 }} />}
          sx={{
            borderRadius: '20px',
            px: 2,
            fontWeight: 600,
            fontSize: '0.8rem',
            whiteSpace: 'nowrap',
            borderColor: theme.palette.divider,
            color: 'text.primary',
            '&:hover': { borderColor: 'primary.main', color: 'primary.main' }
          }}
        >
          Today
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton size="small" onClick={() => onNavigate('PREV')} sx={{ color: 'text.secondary' }}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => onNavigate('NEXT')} sx={{ color: 'text.secondary' }}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
        {view === Views.AGENDA ? (
          <Button
            variant="outlined"
            size="small"
            onClick={handleLabelClick}
            startIcon={<CalendarMonthIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
            endIcon={<ExpandMoreIcon sx={{ fontSize: 16, color: 'text.secondary', opacity: 0.5 }} />}
            sx={{
              borderRadius: '20px',
              px: 2,
              ml: isMobile ? 0 : 1,
              fontWeight: 600,
              fontSize: '0.8rem',
              textTransform: 'none',
              borderColor: theme.palette.divider,
              color: 'text.primary',
              '&:hover': { 
                borderColor: 'primary.main', 
                color: 'primary.main',
                bgcolor: alpha(theme.palette.primary.main, 0.04)
              }
            }}
          >
            {label}
          </Button>
        ) : (
          <Typography
            variant={isMobile ? 'subtitle1' : 'h6'}
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              ml: isMobile ? 0 : 1,
              fontSize: '0.95rem',
            }}
          >
            {label}
          </Typography>
        )}

        {/* Date Range Popover */}
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            paper: {
              sx: {
                mt: 1,
                p: 2.5,
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                minWidth: 300,
              }
            }
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}>
            Select Date Range
          </Typography>
          <Stack spacing={2}>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>
                From
              </Typography>
              <input
                type="date"
                value={rangeStart}
                onChange={(e) => setRangeStart(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: `1px solid ${theme.palette.divider}`,
                  fontSize: '0.9rem',
                  fontFamily: theme.typography.fontFamily,
                  outline: 'none',
                  backgroundColor: 'transparent',
                  color: theme.palette.text.primary,
                }}
              />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>
                To
              </Typography>
              <input
                type="date"
                value={rangeEnd}
                min={rangeStart}
                onChange={(e) => setRangeEnd(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: `1px solid ${theme.palette.divider}`,
                  fontSize: '0.9rem',
                  fontFamily: theme.typography.fontFamily,
                  outline: 'none',
                  backgroundColor: 'transparent',
                  color: theme.palette.text.primary,
                }}
              />
            </Box>
            <Button
              variant="contained"
              size="small"
              onClick={handleApply}
              disabled={!rangeStart || !rangeEnd || isLoading}
              sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, minWidth: 100, height: 32 }}
              startIcon={isLoading ? <CircularProgress size={14} thickness={5} sx={{ color: 'white' }} /> : undefined}
            >
              {isLoading ? 'Apply' : 'Apply Range'}
            </Button>
          </Stack>
        </Popover>
      </Stack>

      {/* View Toggles */}
      <ToggleButtonGroup
        value={view}
        exclusive
        onChange={(_, nextView) => nextView && onView(nextView)}
        size="small"
        sx={{
          bgcolor: alpha(theme.palette.action.hover, 0.04),
          borderRadius: '10px',
          border: `1px solid ${theme.palette.divider}`,
          '& .MuiToggleButton-root': {
            flex: isMobile ? 1 : 'none',
            px: isMobile ? 1.5 : 2,
            py: 0.5,
            fontSize: '0.8rem',
            textTransform: 'none',
            fontWeight: 600,
            border: 'none',
            borderRadius: '8px !important',
            color: 'text.secondary',
            '&.Mui-selected': {
              bgcolor: 'primary.main',
              color: 'white',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              '&:hover': { bgcolor: 'primary.dark' }
            },
            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) }
          }
        }}
      >
        <ToggleButton value={Views.MONTH}>Month</ToggleButton>
        <ToggleButton value={Views.WEEK}>Week</ToggleButton>
        <ToggleButton value={Views.DAY}>Day</ToggleButton>
        <ToggleButton value={Views.AGENDA}>Agenda</ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
};
